import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Grid, Card, CardContent, Button, 
  Fade, Grow, Avatar, IconButton, Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { 
  PersonAdd, EventAvailable, AccessTime, People,
  ArrowForward, PendingActions, WorkOff, TrendingUp
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { getAllAttendance } from '../../api/attendanceAPI';
import { getAllLeaves } from '../../api/leaveAPI';
import { getAllUsers } from '../../api/userAPI';
import moment from 'moment';

const HRDashboard = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.tokens);
  
  const [stats, setStats] = useState({ total: 0, present: 0, pendingLeaves: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchQuickStats = async () => {
      try {
        const [uRes, aRes, lRes] = await Promise.all([
          getAllUsers(),
          getAllAttendance({ range: 'today' }),
          getAllLeaves()
        ]);
        
        let total = 0, present = 0, pending = 0;
        
        if (uRes.data) {
          const activeOfficeEmp = uRes.data.filter(u => u.isOfficeEmp && u.isActive !== false);
          total = activeOfficeEmp.length;
          
          const activeIds = activeOfficeEmp.map(u => u.id || u._id);
          
          if (aRes.data && aRes.data.success) {
            present = aRes.data.log.filter(a => activeIds.includes(a.user?._id)).length;
          }
          
          if (lRes.data && lRes.data.success) {
            pending = lRes.data.leaves.filter(l => activeIds.includes(l.user?._id) && l.status === 'Pending').length;
          }
        }
        
        setStats({ total, present, pendingLeaves: pending });
      } catch (err) {
        console.error("Error fetching HR stats", err);
      }
      setLoading(false);
    };
    
    fetchQuickStats();
  }, []);

  const actionCards = [
    {
      title: "Review Leaves",
      desc: "Check and approve pending leave requests",
      icon: <PendingActions fontSize="large" />,
      color: "#d97706",
      bg: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
      path: "/hr-management" 
    },
    {
      title: "Add Employee",
      desc: "Onboard a new team member to the portal",
      icon: <PersonAdd fontSize="large" />,
      color: "#2563eb",
      bg: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
      path: "/users"
    },
    {
      title: "Manage Teams",
      desc: "View attendance and mark half-days",
      icon: <People fontSize="large" />,
      color: "#059669",
      bg: "linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)",
      path: "/teams"
    },
    {
      title: "Holiday Calendar",
      desc: "Add or manage upcoming company holidays",
      icon: <EventAvailable fontSize="large" />,
      color: "#7c3aed",
      bg: "linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)",
      path: "/holidays"
    }
  ];

  const glassmorphismStyle = {
    background: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.4)',
    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.05)',
  };

  return (
    <Box sx={{ pb: 6 }}>
      {/* QUICK STATS */}
      <Grid container spacing={3} sx={{ mt: 2, mb: 5 }}>
        <Grow in={true} timeout={500}>
          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 4, ...glassmorphismStyle, p: 1 }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Box sx={{ p: 2, borderRadius: '16px', background: '#e0e7ff', color: '#4f46e5' }}>
                  <TrendingUp fontSize="large" />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#64748b', fontWeight: 600 }}>Total Workforce</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e293b' }}>{stats.total}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grow>

        <Grow in={true} timeout={700}>
          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 4, ...glassmorphismStyle, p: 1 }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Box sx={{ p: 2, borderRadius: '16px', background: '#dcfce7', color: '#16a34a' }}>
                  <People fontSize="large" />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#64748b', fontWeight: 600 }}>Present Today</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e293b' }}>{stats.present}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grow>

        <Grow in={true} timeout={900}>
          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 4, ...glassmorphismStyle, p: 1 }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Box sx={{ p: 2, borderRadius: '16px', background: '#fef3c7', color: '#d97706' }}>
                  <PendingActions fontSize="large" />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#64748b', fontWeight: 600 }}>Pending Leaves</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e293b' }}>{stats.pendingLeaves}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grow>
      </Grid>

      <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', mb: 3, fontFamily: '"Outfit", sans-serif' }}>
        Quick Actions
      </Typography>

      <Grid container spacing={3}>
        {actionCards.map((card, idx) => (
          <Grow in={true} timeout={600 + (idx * 200)} key={idx}>
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                onClick={() => navigate(card.path)}
                sx={{ 
                  borderRadius: 4, 
                  background: card.bg,
                  cursor: 'pointer',
                  border: 'none',
                  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.05)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                  },
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ 
                  position: 'absolute', top: -20, right: -20,
                  width: 100, height: 100, borderRadius: '50%',
                  background: 'rgba(255,255,255,0.4)', zIndex: 0
                }} />
                <CardContent sx={{ p: 3, position: 'relative', zIndex: 1, minHeight: 160, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ color: card.color, mb: 2 }}>
                    {card.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
                    {card.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#475569', flexGrow: 1 }}>
                    {card.desc}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <IconButton size="small" sx={{ background: 'rgba(255,255,255,0.5)', color: card.color }}>
                      <ArrowForward fontSize="small" />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grow>
        ))}
      </Grid>
    </Box>
  );
};

export default HRDashboard;
